import { parse, format } from "date-fns";

export function toIso(text: string): string | null {
  const patterns = [
    // Numeric day / month / year (slashes, dashes, dots)
    "d/M/yyyy",
    "d/M/yy",
    "dd/MM/yyyy",
    "dd/MM/yy",
    "d-M-yyyy",
    "d-M-yy",
    "dd-MM-yyyy",
    "dd-MM-yy",
    "d.M.yyyy",
    "d.M.yy",
    "dd.MM.yyyy",
    "dd.MM.yy",
    "yyyy-MM-dd",
    "yyyy-M-d",
    "yy-MM-dd",
    "yy-M-d",

    // Day-Month-name-Year (spaces or dashes)
    "d MMM yyyy",
    "d MMM yy",
    "dd MMM yyyy",
    "dd MMM yy",
    "d MMMM yyyy",
    "d MMMM yy",
    "dd MMMM yyyy",
    "dd MMMM yy",
    "d-MMM-yyyy",
    "d-MMM-yy",
    "dd-MMM-yyyy",
    "dd-MMM-yy",
    "d-MMMM-yyyy",
    "d-MMMM-yy",
    "dd-MMMM-yyyy",
    "dd-MMMM-yy",

  ];

  const trimmed = text.trim();

  for (const p of patterns) {
    const d = parse(trimmed, p, new Date());

    // parse() → Invalid Date if pattern doesn't match
    if (isNaN(d.getTime())) continue;

    // Normalise two-digit years:
    //   00-69 → 2000-2069, 70-99 → 1970-1999
    let year = d.getFullYear();
    if (year >= 0 && year <= 99) {
      year += year <= 69 ? 2000 : 1900;
      d.setFullYear(year);
    }

    // Return canonical yyyy-MM-dd
    return format(d, "yyyy-MM-dd");
  }

  return null; // nothing matched
}

function extractDatesFromText(text: string): string[] {
  const candidates = [
    /\b\d{4}-\d{1,2}-\d{1,2}\b/g,
    /\b\d{1,2}[./-]\d{1,2}[./-]\d{2,4}\b/g,
    /\b\d{1,2}[\s-]+[A-Za-z]{3,9}[\s-]+\d{2,4}\b/g,
  ];

  const seen = new Set<string>();
  const matches: Array<{ index: number; value: string }> = [];

  for (const re of candidates) {
    for (const match of text.matchAll(re)) {
      if (match.index === undefined) continue;
      const value = match[0];
      const key = `${match.index}:${value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      matches.push({ index: match.index, value });
    }
  }

  matches.sort((a, b) => a.index - b.index);

  const dates: string[] = [];
  for (const match of matches) {
    const iso = toIso(match.value);
    if (iso) dates.push(iso);
  }

  return dates;
}

export function parsePeriodRange(
  text: string
): { from: string; to: string } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const cleaned = trimmed.replace(/^period\s*:/i, "").trim();
  const dates = extractDatesFromText(cleaned);
  if (dates.length < 2) return null;

  return { from: dates[0], to: dates[1] };
}

export function parseAmountFromText(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const negativeHint =
    /\(\s*[^)]*\d/.test(trimmed) ||
    /-\s*[^0-9]*\d/.test(trimmed) ||
    /\d\s*-\s*$/.test(trimmed);

  let cleaned = trimmed.replace(/[^0-9,.\-]+/g, " ");
  cleaned = cleaned.replace(/\s+/g, " ").trim();
  cleaned = cleaned.replace(/-\s+(?=\d)/g, "-");

  const match = cleaned.match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!match) return null;

  const numeric = match[0].replace(/,/g, "");
  const value = Number.parseFloat(numeric);
  if (Number.isNaN(value)) return null;

  if (negativeHint && value > 0) {
    return -value;
  }

  return value;
}
